import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, Phone, Building2, MapPin, Calendar, Pencil, Trash2 } from "lucide-react";
import EmployeeFormDialog from "@/components/directory/EmployeeFormDialog";
import OffboardConfirmDialog from "@/components/directory/OffboardConfirmDialog";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 border-b border-[#efefe9] py-3 last:border-b-0">
    <Icon strokeWidth={1.5} className="mt-0.5 h-4 w-4 text-[#8a8a80]" />
    <div className="flex-1">
      <div className="overline">{label}</div>
      <div className="mt-0.5 text-sm text-[#1c1c19]">{value}</div>
    </div>
  </div>
);

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [emp, setEmp] = useState(null);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [offboardOpen, setOffboardOpen] = useState(false);

  const load = () =>
    api
      .get(`/employees/${id}`)
      .then((r) => setEmp(r.data))
      .catch(() => setErr("Employee not found"));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onOffboard = async () => {
    try {
      await api.delete(`/employees/${id}`);
      toast.success(`${emp.name} offboarded`);
      navigate("/app/directory", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Offboard failed");
    }
  };

  if (err) return <div className="text-sm text-[#575752]">{err}</div>;
  if (!emp) return <div className="overline">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="employee-detail-page">
      <div className="flex items-center justify-between">
        <Link
          to="/app/directory"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#575752] hover:text-[#1c1c19]"
        >
          <ArrowLeft strokeWidth={1.5} className="h-3.5 w-3.5" /> Back to directory
        </Link>
        {isAdmin && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="detail-edit-btn"
              onClick={() => setEditOpen(true)}
              className="border-[#e5e5e0]"
            >
              <Pencil strokeWidth={1.5} className="mr-1.5 h-3.5 w-3.5" /> Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="detail-offboard-btn"
              onClick={() => setOffboardOpen(true)}
              className="border-[#e5e5e0] text-[#8f2c2c] hover:bg-[#fcecec]"
            >
              <Trash2 strokeWidth={1.5} className="mr-1.5 h-3.5 w-3.5" /> Offboard
            </Button>
          </div>
        )}
      </div>

      <header className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="rounded-lg border border-[#e5e5e0] bg-white p-6">
            <Avatar className="h-24 w-24 rounded-md">
              <AvatarImage src={emp.avatar} alt={emp.name} />
              <AvatarFallback className="rounded-md">
                {emp.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </AvatarFallback>
            </Avatar>
            <h1 className="mt-5 font-display text-2xl font-bold tracking-tight">
              {emp.name}
            </h1>
            <div className="mt-1 font-mono text-xs text-[#575752]">
              {emp.role_title}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-full bg-[#f0f0ed] px-2 py-0.5 text-[11px] font-medium text-[#575752]">
                {emp.department}
              </span>
              <span className="rounded-full bg-[#eaf0ec] px-2 py-0.5 text-[11px] font-medium text-[#3d5a45]">
                {emp.office}
              </span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="rounded-lg border border-[#e5e5e0] bg-white p-6">
            <div className="overline">About</div>
            <p className="mt-2 text-sm leading-relaxed text-[#1c1c19]">
              {emp.bio || <span className="text-[#8a8a80]">No bio yet.</span>}
            </p>
            <div className="mt-6">
              <Row icon={Mail} label="Email" value={emp.email} />
              <Row icon={Phone} label="Phone" value={emp.phone || "—"} />
              <Row icon={Building2} label="Department" value={emp.department} />
              <Row icon={MapPin} label="Office" value={emp.office} />
              <Row icon={Calendar} label="Joined" value={emp.joined_at} />
              {emp.manager && <Row icon={Building2} label="Reports to" value={emp.manager} />}
            </div>
          </div>
        </div>
      </header>

      <EmployeeFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        employee={emp}
        onSaved={(updated) => setEmp(updated)}
      />
      <OffboardConfirmDialog
        open={offboardOpen}
        onOpenChange={setOffboardOpen}
        employeeName={emp.name}
        onConfirm={onOffboard}
      />
    </div>
  );
}
